import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie } from 'lucide-react';

const KEY = 'pa_analytics_consent';

const setConsent = (value) => {
  try {
    localStorage.setItem(KEY, value);
  } catch { /* storage blocked — the choice just will not persist */ }
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      analytics_storage: value === 'granted' ? 'granted' : 'denied',
    });
  }
};

/**
 * Short analytics notice. Google Analytics starts denied (see index.html) and
 * is only granted here, so what the privacy policy says matches what actually
 * happens. Reopened from the footer's "Cookie settings" link.
 */
export const CookieNotice = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let stored = null;
    try {
      stored = localStorage.getItem(KEY);
    } catch { /* storage blocked */ }
    if (!stored) setOpen(true);
    const reopen = () => setOpen(true);
    window.addEventListener('open-cookie-notice', reopen);
    return () => window.removeEventListener('open-cookie-notice', reopen);
  }, []);

  if (!open) return null;

  const choose = (value) => {
    setConsent(value);
    setOpen(false);
  };

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[60] p-3 sm:p-4 animate-in slide-in-from-bottom duration-300"
      role="region"
      aria-label="Cookie notice"
      data-testid="cookie-notice"
    >
      <div className="max-w-3xl mx-auto rounded-xl bg-slate-900/95 backdrop-blur-md ring-1 ring-white/10 shadow-2xl p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <Cookie size={18} className="text-cyan-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-slate-200 leading-relaxed">
              We use Google Analytics to count visits and see which stories are read. Nothing is
              used to identify you, and we run no advertising cookies. Read our{' '}
              <Link to="/privacy" className="text-cyan-400 underline hover:text-cyan-300">
                privacy policy
              </Link>.
            </p>
            <div className="mt-3.5 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => choose('granted')}
                className="text-sm font-semibold px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-900 transition-colors"
                data-testid="cookie-accept"
              >
                Allow analytics
              </button>
              <button
                type="button"
                onClick={() => choose('denied')}
                className="text-sm font-semibold px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-slate-100 transition-colors"
                data-testid="cookie-decline"
              >
                No thanks
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieNotice;
